import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Printer } from "@phosphor-icons/react";
import { InlineQR } from "@/components/QRCodeButton";
import { SupportingTendersView } from "@/components/SupportingTendersEditor";

export default function InvoicePrint() {
  const { id } = useParams();
  const { activeCompanyId, companies } = useAuth();
  const [inv, setInv] = useState(null);

  useEffect(() => {
    api.get(`/sales-invoices/${id}`).then((r) => setInv(r.data));
  }, [id, activeCompanyId]);

  if (!inv) return <div className="p-10 text-sm">Loading…</div>;
  const company = companies.find((c) => c.id === inv.company_id) || companies[0] || {};
  const intra = (inv.cgst_total || 0) + (inv.sgst_total || 0) > 0;

  return (
    <div className="min-h-screen bg-white text-black print:bg-white" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="max-w-4xl mx-auto p-10 print:p-6">
        {/* Toolbar (hidden on print) */}
        <div className="flex justify-end mb-6 print:hidden gap-2">
          <Button onClick={() => window.print()} className="h-9"><Printer size={14} className="mr-1.5" /> Print / Save PDF</Button>
        </div>

        <div className="border-2 border-black">
          {/* Header */}
          <div className="p-6 border-b-2 border-black flex items-center justify-between gap-6">
            <img
              src={company.logo_url || "/brand/vetrich-logo.png"}
              alt={company.name || "Logo"}
              style={{ height: "96px", width: "auto", objectFit: "contain", flexShrink: 0 }}
              crossOrigin="anonymous"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>Tax Invoice</div>
              <h1 className="text-2xl font-bold tracking-tight mt-1" style={{ fontFamily: "'Chivo', sans-serif" }}>{company.name || "Vetrich"}</h1>
              <div className="text-[12px] text-gray-600 mt-1.5 whitespace-pre-line">{company.address}</div>
              <div className="text-[12px] mt-1">GSTIN: <span className="font-mono">{company.gstin}</span> · State: {company.state} ({company.state_code})</div>
              <div className="text-[12px]">Phone: {company.phone} · Email: {company.email}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>Invoice #</div>
              <div className="text-lg font-mono font-bold mt-1">{inv.number}</div>
              <div className="mt-2 text-[12px]">Date: <span className="font-mono">{inv.date}</span></div>
              {inv.share_token && (
                <div className="mt-3 flex justify-end">
                  <InlineQR
                    url={`${window.location.origin}/p/invoice/${inv.id}?t=${inv.share_token}`}
                    size={72}
                    caption="Scan"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Bill to */}
          <div className="p-6 grid grid-cols-2 gap-8 border-b-2 border-black">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5" style={{ fontFamily: "'Chivo', sans-serif" }}>Bill To</div>
              <div className="font-bold text-base" data-testid="bill-to-name">{inv.customer_name}</div>
              {inv.customer_phone && <div className="text-[12px] mt-1" data-testid="bill-to-phone">Mobile: <span className="font-mono">{inv.customer_phone}</span></div>}
              {inv.customer_address && <div className="text-[12px] mt-0.5 whitespace-pre-line" data-testid="bill-to-address">{inv.customer_address}</div>}
              {inv.customer_gstin && <div className="text-[12px] mt-1">GSTIN: <span className="font-mono">{inv.customer_gstin}</span></div>}
              {inv.customer_state_code && <div className="text-[12px]">State Code: <span className="font-mono">{inv.customer_state_code}</span></div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5" style={{ fontFamily: "'Chivo', sans-serif" }}>Payment</div>
              <div className="text-[13px]">Status: <span className="font-semibold uppercase">{inv.status}</span></div>
              <div className="text-[13px]">Paid: <span className="font-mono">{inr(inv.paid_amount)}</span></div>
              {(inv.payment_splits || []).length > 0 ? (() => {
                const uniqueModes = Array.from(new Set(
                  inv.payment_splits.map((sp) => (sp.mode || "").trim().toUpperCase().replace(/_/g, " "))
                )).filter(Boolean);
                return (
                  <>
                    <div className="text-[13px]">
                      Modes: <span className="font-semibold font-mono">{uniqueModes.join(" · ")}</span>
                    </div>
                    <div className="mt-1 border border-gray-300 rounded">
                      <table className="w-full text-[11.5px]">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left px-2 py-1 font-semibold">Mode</th>
                            <th className="text-right px-2 py-1 font-semibold">Amount</th>
                            <th className="text-left px-2 py-1 font-semibold">UPI ID / Transaction ID / UTR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inv.payment_splits.map((sp, i) => (
                            <tr key={i} className="border-t border-gray-200">
                              <td className="px-2 py-1 uppercase">{String(sp.mode || "").replace(/_/g, " ")}</td>
                              <td className="px-2 py-1 font-mono text-right">{inr(sp.amount || 0)}</td>
                              <td className="px-2 py-1 font-mono text-gray-600">{sp.reference || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })() : (inv.payment_mode || "").trim() ? (
                <div className="text-[13px]">
                  Mode: <span className="font-semibold uppercase">{String(inv.payment_mode).replace(/_/g, " ")}</span>
                  {inv.payment_reference ? <span className="font-mono text-gray-600"> · {inv.payment_reference}</span> : null}
                </div>
              ) : null}
              <div className="text-[13px]">Balance Due: <span className="font-mono font-semibold">{inr(inv.balance_due)}</span></div>
            </div>
          </div>

          {/* Items table */}
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b-2 border-black" style={{ fontFamily: "'Chivo', sans-serif" }}>
                <th className="text-left p-3 uppercase text-[10px] tracking-widest">#</th>
                <th className="text-left p-3 uppercase text-[10px] tracking-widest">Description</th>
                <th className="text-left p-3 uppercase text-[10px] tracking-widest">HSN</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Qty</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Rate</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Disc</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Tax</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Total</th>
              </tr>
            </thead>
            <tbody>
              {inv.items.map((it, i) => (
                <tr key={it.product_id || `${it.name}-${it.hsn || ''}-${i}`} className="border-b border-gray-300">
                  <td className="p-3 font-mono">{i + 1}</td>
                  <td className="p-3">{it.name}</td>
                  <td className="p-3 font-mono">{it.hsn}</td>
                  <td className="p-3 text-right font-mono">{it.quantity} {it.unit}</td>
                  <td className="p-3 text-right font-mono">{inr(it.rate)}</td>
                  <td className="p-3 text-right font-mono">{it.discount_pct}%</td>
                  <td className="p-3 text-right font-mono">{it.tax_rate}%</td>
                  <td className="p-3 text-right font-mono font-semibold">{inr(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Summary */}
          <div className="border-t-2 border-black flex">
            <div className="flex-1 p-6 text-[12px]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Notes / Work Name</div>
              <div className="whitespace-pre-line">{inv.notes || "—"}</div>
              <div className="mt-6 text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1" style={{ fontFamily: "'Chivo', sans-serif" }}>Terms</div>
              <div>Payment due within 30 days of invoice date. Interest @18% p.a on overdue amounts. Subject to jurisdiction.</div>
            </div>
            <div className="w-72 border-l-2 border-black p-6 text-[13px] space-y-1.5">
              <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-mono">{inr(inv.subtotal)}</span></div>
              {intra ? (
                <>
                  <div className="flex justify-between"><span className="text-gray-600">CGST</span><span className="font-mono">{inr(inv.cgst_total)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">SGST</span><span className="font-mono">{inr(inv.sgst_total)}</span></div>
                </>
              ) : (
                <div className="flex justify-between"><span className="text-gray-600">IGST</span><span className="font-mono">{inr(inv.igst_total)}</span></div>
              )}
              <div className="border-t-2 border-black pt-2 mt-2 flex justify-between font-bold text-base" style={{ fontFamily: "'Chivo', sans-serif" }}>
                <span>Grand Total</span><span className="font-mono">{inr(inv.grand_total)}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t-2 border-black">
            <SupportingTendersView items={inv.supporting_tenders} />
          </div>
          <div className="p-6 border-t-2 border-black flex items-end justify-between">
            <div className="text-[11px] text-gray-600">
              This is a computer-generated invoice. E&OE.
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>Authorised Signatory</div>
              <div className="mt-8 text-[12px] font-semibold">For {company.name}</div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>
          Generated by Vetrich Technologies · Smart Accounting. Smarter Business.
        </div>
      </div>
    </div>
  );
}
